/**
 * Utility to generate a color table from order data
 * This can be used in the admin, API, or custom app to extract color information
 */

class CanopyColorTable {
  constructor(colorData) {
    this.colorData = typeof colorData === 'string' ? JSON.parse(colorData) : colorData;
  }

  /**
   * Generate an HTML table with panel colors
   * @param {Object} options - Formatting options
   * @returns {String} HTML table
   */
  generateHtmlTable(options = {}) {
    const defaultOptions = {
      includeHexCodes: true,
      includeColorSwatches: true,
      tableClass: 'color-table',
      headerBgColor: '#f5f5f5'
    };
    const opts = { ...defaultOptions, ...options };
    
    let tableHtml = `<table class="${opts.tableClass}" style="width: 100%; border-collapse: collapse; margin: 20px 0;">
                      <thead style="background-color: ${opts.headerBgColor};">
                        <tr>
                          <th style="padding: 8px; text-align: left; border: 1px solid #ddd;">Panel ID</th>
                          <th style="padding: 8px; text-align: left; border: 1px solid #ddd;">Color Name</th>`;
    
    if (opts.includeHexCodes) {
      tableHtml += `<th style="padding: 8px; text-align: left; border: 1px solid #ddd;">Hex Code</th>`;
    }
    
    if (opts.includeColorSwatches) {
      tableHtml += `<th style="padding: 8px; text-align: left; border: 1px solid #ddd;">Color</th>`;
    }
    
    tableHtml += `</tr></thead><tbody>`;

    // Add rows for each panel
    for (const [panelId, colorInfo] of Object.entries(this.colorData)) {
      const colorName = typeof colorInfo === 'object' ? colorInfo.name : 'Unknown';
      const hexCode = typeof colorInfo === 'object' ? colorInfo.hex : colorInfo;
      
      tableHtml += `<tr>
                      <td style="padding: 8px; text-align: left; border: 1px solid #ddd;">${panelId}</td>
                      <td style="padding: 8px; text-align: left; border: 1px solid #ddd;">${colorName}</td>`;
      
      if (opts.includeHexCodes) {
        tableHtml += `<td style="padding: 8px; text-align: left; border: 1px solid #ddd;">${hexCode}</td>`;
      }
      
      if (opts.includeColorSwatches) {
        tableHtml += `<td style="padding: 8px; text-align: left; border: 1px solid #ddd;">
                        <div style="width: 20px; height: 20px; background-color: ${hexCode}; border: 1px solid #ccc;"></div>
                      </td>`;
      }
      
      tableHtml += `</tr>`;
    }
    
    tableHtml += `</tbody></table>`;
    
    return tableHtml;
  }

  /**
   * Generate a text-only table of color data
   * @returns {String} Plain text table
   */
  generateTextTable() {
    let textTable = "Panel ID | Color Name | Hex Code\n";
    textTable += "---------|------------|----------\n";
    
    for (const [panelId, colorInfo] of Object.entries(this.colorData)) {
      const colorName = typeof colorInfo === 'object' ? colorInfo.name : 'Unknown';
      const hexCode = typeof colorInfo === 'object' ? colorInfo.hex : colorInfo;
      
      textTable += `${panelId.padEnd(9)} | ${colorName.padEnd(10)} | ${hexCode}\n`;
    }
    
    return textTable;
  }

  /**
   * Get a JSON representation of the color data with names
   * @returns {Object} Structured color data
   */
  toJson() {
    // Convert any legacy format (just hex strings) to new format (objects with name and hex)
    const structured = {};
    
    for (const [panelId, colorInfo] of Object.entries(this.colorData)) {
      if (typeof colorInfo === 'string') {
        structured[panelId] = {
          name: 'Unknown', 
          hex: colorInfo
        };
      } else {
        structured[panelId] = colorInfo;
      }
    }
    
    return structured;
  }

  /**
   * Get CSV data for export
   * @returns {String} CSV formatted data
   */
  toCsv() {
    let csv = "Panel ID,Color Name,Hex Code\n";
    
    for (const [panelId, colorInfo] of Object.entries(this.colorData)) {
      const colorName = typeof colorInfo === 'object' ? colorInfo.name : 'Unknown';
      const hexCode = typeof colorInfo === 'object' ? colorInfo.hex : colorInfo;
      
      // Escape values if they contain commas
      const escapedName = colorName.includes(',') ? `"${colorName}"` : colorName;
      
      csv += `${panelId},${escapedName},${hexCode}\n`;
    }
    
    return csv;
  }
}

// Use in browser or export for Node.js
if (typeof module !== 'undefined' && module.exports) {
  module.exports = CanopyColorTable;
} else if (typeof window !== 'undefined') {
  window.CanopyColorTable = CanopyColorTable;
} 